/* iter-alias-claim — First-login modal to pick a @crownnrose.com alias.
 *
 * Shown on the first login of any user whose `pen_slug` is null.
 * Reads `GET /api/inbox/my-alias`; if `slug` still equals the fallback
 * (name-derived or user-id tail) we assume the user hasn't claimed one
 * yet and pop this modal. The user can either pick a slug or dismiss
 * (dismiss sets a session flag so we don't nag on every navigation).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle2, XCircle, Loader2, AtSign } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth_context';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const DISMISS_KEY = 'cr:alias-claim-dismissed';

export default function AliasClaimModal() {
  const { user, checking: authChecking } = useAuth();
  const [open,    setOpen]    = useState(false);
  const [slug,    setSlug]    = useState('');
  const [state,   setState]   = useState(null);   // {available, reason, suggestions}
  const [checking,setChecking]= useState(false);
  const [claiming,setClaiming]= useState(false);
  const [alias,   setAlias]   = useState(null);   // current alias info

  useEffect(() => {
    if (authChecking || !user) return;
    if (sessionStorage.getItem(DISMISS_KEY)) return;
    axios.get(`${API}/inbox/my-alias`, { withCredentials: true })
      .then((r) => {
        setAlias(r.data);
        // Show the modal if the user hasn't picked a slug — heuristic:
        // slug either equals `user` or has a trailing `-<user_id tail>`
        // (both are auto-generated fallbacks).
        const s = r.data?.slug || '';
        const looksAuto = /^user(-|$)|-[a-z0-9]{6}$/.test(s);
        if (looksAuto) { setOpen(true); setSlug(s.replace(/-[a-z0-9]{6}$/, '')); }
      })
      .catch(() => { /* not logged in — silently skip */ });
  }, [authChecking, user]);

  const check = useCallback(async (v) => {
    setChecking(true);
    try {
      const r = await axios.get(`${API}/aliases/check`, {
        params: { slug: v }, withCredentials: true,
      });
      setState(r.data);
    } catch { setState(null); }
    finally { setChecking(false); }
  }, []);

  useEffect(() => {
    if (!open || !slug || slug.length < 3) { setState(null); return; }
    const t = setTimeout(() => check(slug), 350);
    return () => clearTimeout(t);
  }, [open, slug, check]);

  const claim = async (chosen) => {
    setClaiming(true);
    try {
      const r = await axios.post(`${API}/aliases/claim`,
        { slug: chosen }, { withCredentials: true });
      toast.success(`Your alias ${r.data.alias} is live.`);
      setOpen(false);
      // Fire a page refresh so the inbox header re-reads the new alias.
      setTimeout(() => window.location.reload(), 700);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Claim failed.');
    } finally { setClaiming(false); }
  };

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setOpen(false);
    toast.info('You can claim your alias later from the Inbox header.');
  };

  if (!open) return null;
  const available = state?.available;

  return (
    <Dialog open={open} onOpenChange={(v) => v || dismiss()}>
      <DialogContent data-testid="alias-claim-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AtSign className="h-4 w-4 text-primary" />
            Claim your Crown &amp; Rose email
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-[12.5px]">
          <p className="text-muted-foreground">
            Pick the name you'd like on your <code>@crownnrose.com</code> address.
            You'll receive email at this alias in the Loom's inbox — replies from
            paid tiers go out from the same address.
          </p>
          <div className="flex items-center gap-1.5">
            <Input
              autoFocus
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="yourname"
              maxLength={40}
              className="h-9 font-mono-stat"
              data-testid="alias-claim-slug-input"
            />
            <span className="text-[13px] text-muted-foreground">@crownnrose.com</span>
          </div>
          <div className="min-h-[24px] text-[11.5px]">
            {checking && (
              <span className="flex items-center gap-1 text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> Checking…
              </span>
            )}
            {!checking && state && available && (
              <span className="flex items-center gap-1 text-emerald-700 dark:text-emerald-400"
                    data-testid="alias-claim-available">
                <CheckCircle2 className="h-3.5 w-3.5" />
                <code>{state.slug}@crownnrose.com</code> is available.
              </span>
            )}
            {!checking && state && !available && (
              <div data-testid="alias-claim-unavailable">
                <span className="flex items-center gap-1 text-rose-700 dark:text-rose-400">
                  <XCircle className="h-3.5 w-3.5" />
                  {state.reason === 'reserved'
                    ? <>Reserved for platform use.</>
                    : state.reason === 'invalid_format'
                    ? <>Use 3-40 chars, lowercase letters/digits/hyphens.</>
                    : <>Taken.</>}
                </span>
                {state.suggestions?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {state.suggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSlug(s)}
                        className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10.5px] font-mono-stat hover:bg-muted"
                        data-testid={`alias-claim-suggest-${s}`}
                      >{s}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-between gap-2">
            <Button size="sm" variant="ghost" onClick={dismiss}
                    data-testid="alias-claim-dismiss">
              Later
            </Button>
            <Button size="sm" onClick={() => claim(slug)}
                    disabled={!available || claiming}
                    data-testid="alias-claim-submit">
              {claiming ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              Claim {slug ? `${slug}@crownnrose.com` : 'alias'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
